import { Notification } from "@/models/notification/Notifications";

/**
 * Date part only (YYYY-MM-DD) for grouping by day, using the given timezone (default local).
 */
function toDateString(isoDate: string): string {
  const d = new Date(isoDate);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Stable group key for a notification: same document and same day.
 * When documentId is null/empty, use notification id so each stays in its own group (no grouping nulls together).
 */
function getGroupKey(notification: Notification): {
  date: string;
  documentKey: string;
  sortDate: string;
} {
  const ts = notification.updatedAt ?? notification.createdAt;
  const dateStr = toDateString(ts);
  const id = notification.documentId;
  const documentKey =
    id != null && id.trim() !== "" ? id : notification.id;
  return {
    date: dateStr,
    documentKey,
    sortDate: ts,
  };
}

export type NotificationGroup = {
  /** Unique key for the group (for React key and dismiss group). */
  groupKey: string;
  /** Display label for the collapsible header (document name or fallback from notification content). */
  label: string;
  /** Notifications in this group, newest first. */
  notifications: Notification[];
  /** Sort key: date descending, then by first notification time. */
  sortKey: string;
};

/**
 * Derive a display label from the first notification in a group (document name or fallbacks).
 */
function getGroupLabel(first: Notification): string {
  if (first.documentName != null && first.documentName.trim() !== "") {
    return first.documentName.trim();
  }
  if (first.documentId != null && first.documentId.trim() !== "") {
    return first.documentId;
  }
  if (first.itemsChanged?.length && first.itemsChanged[0]) {
    return first.itemsChanged[0];
  }
  if (first.text?.trim()) {
    const maxLen = 60;
    return first.text.length <= maxLen
      ? first.text
      : `${first.text.slice(0, maxLen).trim()}…`;
  }
  return "Notifikation";
}

/**
 * Groups notifications by same day (from updatedAt) and same document ID.
 * Notifications with null/empty documentId are not grouped together; each gets its own group.
 * Group title is derived from document name or notification content (itemsChanged, text, etc.).
 * Returns groups sorted by date descending, then by latest notification in group.
 */
export function groupNotificationsByDocumentAndDay(
  notifications: Notification[]
): NotificationGroup[] {
  const map = new Map<
    string,
    { date: string; documentKey: string; sortDate: string; list: Notification[] }
  >();

  for (const n of notifications) {
    const { date, documentKey, sortDate } = getGroupKey(n);
    const key = `${date}\t${documentKey}`;
    const existing = map.get(key);
    if (existing) {
      existing.list.push(n);
      if (sortDate > existing.sortDate) existing.sortDate = sortDate;
    } else {
      map.set(key, {
        date,
        documentKey,
        sortDate,
        list: [n],
      });
    }
  }

  const groups: NotificationGroup[] = [];
  for (const [, value] of map) {
    value.list.sort(
      (a, b) =>
        new Date(b.updatedAt ?? b.createdAt).getTime() -
        new Date(a.updatedAt ?? a.createdAt).getTime()
    );
    const first = value.list[0];
    const label = getGroupLabel(first);
    groups.push({
      groupKey: `${value.date}\t${value.documentKey}`,
      label,
      notifications: value.list,
      sortKey: `${value.date}\t${value.sortDate}`,
    });
  }

  groups.sort((a, b) => (b.sortKey > a.sortKey ? 1 : -1));
  return groups;
}